import React, { useState } from 'react';
import { registerClass, currySC, IFNTheme, cns } from '@food/css-manager';
import { Link } from 'react-router-dom';
import { Query } from 'react-apollo';
import { Layouts, Slider } from '@food/ui/build/dist/src/Slider';
import { MagazineSpinner } from './MagazineSpinner';
import { nodes } from '../utils/data';
import { MagazineCard } from './MagazineCard';
import { DefaultLayout } from '../utils/layout';
import { MagazineCollection } from '../server-types';
import { Select } from '@food/ui';
import { useTranslation } from 'react-i18next';
import { queryStringValue } from '../utils/misc';

interface IMagazineSlideshowProps {
	layout: DefaultLayout;
	className?: string;
	query: any;
	variables: any;
	sectionTitle?: string;
	excluded?: ReadonlyArray<string>;
	collection?: MagazineCollection;
	preSelectedCategories?: any[] | null;
}

// sliderContainer
const styleClass = registerClass(
	(t: IFNTheme) => `

    &.${sc('layout', DefaultLayout.TABLET)},
    &.${sc('layout', DefaultLayout.MOBILE)} {
        padding-right: 0;
        padding-left: 0;
    }
    
	.${sc('sectionTitle')} {
		margin-bottom: ${t.ratios.s * t.font.baseSize}px;
		display: flex;
		align-items: center;

		> a {
			font-size: ${t.ratios.xl}em;
			font-weight: ${t.font.weights.bold};
		}

		.${sc('categorySelect')} {
			margin-left: auto;
			border: 1px solid ${t.colors.alt.plain};
			border-radius: ${t.radius * t.font.baseSize}px;
			flex-shrink: 0;
		}
		
	}

	&.${sc('layout', DefaultLayout.MOBILE)} {
		.${sc('sectionTitle')} {
			justify-content: center;
			flex-direction: column;

			.${sc('categorySelect')} {
				margin-left: 0;
				margin-top: ${t.ratios.l}em;
			}
		}
	}
`,
);

const sc = currySC(styleClass);

export const MagazineSlideshow: React.FunctionComponent<IMagazineSlideshowProps> = ({
	className,
	query,
	variables,
	layout,
	sectionTitle,
	excluded = [],
	collection,
	preSelectedCategories
}) => {
	const { t } = useTranslation();

	const collectionCategories = collection?.categoriesWithCounts;
	const selectOptions =
		collectionCategories && collectionCategories.length > 0
			? [
				{
					label: t`show_all`,
					value: '',
				},
				...collectionCategories.map((p) => ({
					label: `${p.name} (${p.count})`,
					value: p.name,
				})),
			]
			: [];


	let qsCategoryMatch: any[] = [];

	if (preSelectedCategories) {
		if (qsCategoryMatch.length === 0 && preSelectedCategories[0]) qsCategoryMatch = selectOptions.filter((item) => queryStringValue(item.value) === preSelectedCategories[0]);
		if (qsCategoryMatch.length === 0 && preSelectedCategories[1]) qsCategoryMatch = selectOptions.filter((item) => queryStringValue(item.value) === preSelectedCategories[1]);
	}

	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
		qsCategoryMatch?.length ? qsCategoryMatch[0].value : undefined,
	);
	return (
		<section className={cns(styleClass, className, sc('layout-' + layout))}>
			<h2 className={cns(sc('sectionTitle'))}>
				<Link to={'/edicola/' + (collection?.id || '')}>
					{sectionTitle ? sectionTitle : collection ? collection.name : ''}
				</Link>
				{selectOptions.length > 1 ? (
					<Select
						className={cns(sc('categorySelect'))}
						options={selectOptions}
						selected={selectedCategory}
						onChange={(value: string) => setSelectedCategory(value)}
					></Select>
				) : null}
			</h2>
			<Query<
				any,
				{
					filter: {
						MagazineCollection: {
							id: string;
						};
						category: string;
					};
				}
			>
				query={query}
				variables={{
					...variables,
					filter: {
						...variables.filter,
						category: selectedCategory,
					},
				}}
			>
				{({ error, loading, data }) => {
					if (error) {
						// TODO
						console.error(error);
						return <div />;
					}

					if (loading) {
						const loadingCard = new Array(10).fill(null);
						return (
							<Slider
								layout={
									layout === 'mobile'
										? Layouts.SINGLE_ELEMENT
										: Layouts.SINGLE_ROW
								}
								key={'loading'}
							>
								{loadingCard.map((_, index: number) => (
									<MagazineSpinner key={index} />
								))}
							</Slider>
						);
					}

					const magazines = nodes(data.magazines).filter(
						(magazine) => excluded.indexOf(magazine.id) === -1,
					);

					return (
						<Slider
							layout={
								layout === 'mobile' ? Layouts.SINGLE_ELEMENT : Layouts.SINGLE_ROW
							}
							key={'loaded'}
						>
							{magazines.map((mag: any) => (
								<MagazineCard
									className={cns(sc('card'))}
									key={mag.id}
									magazine={mag}
									layout={layout}
								/>
							))}
						</Slider>
					);
				}}
			</Query>
		</section>
	);
};
